import React, { useEffect, useState, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Platform,
  Modal,
  ScrollView,
  Dimensions } from 'react-native'
import { ArrowLeft, ArrowRight, X, Store } from 'lucide-react-native'
import Svg, { Rect, Text as SvgText, G, Line, Circle } from 'react-native-svg'
import { Calendar, DateData } from 'react-native-calendars'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'VisualSpaceBrowse'>
  route: RouteProp<RootStackParamList, 'VisualSpaceBrowse'>
}

interface Floor {
  id: string
  name: string
  width_cm: number
  height_cm: number
  background_image_url: string | null
  sort_order: number
}

interface SpotType {
  id: string
  name: string
  width_cm: number
  depth_cm: number
  color: string
  price_per_day: number | null
  price_per_week: number | null
  price_per_month: number | null
}

interface Spot {
  id: string
  floor_id: string
  spot_type_id: string
  spot_number: string
  x_position: number
  y_position: number
  rotation: number
  is_available: boolean
  is_active: boolean
  notes: string | null
  spot_type?: SpotType
}

interface Reservation {
  start_date: string
  end_date: string
  status: string
}

interface Market {
  id: string
  name: string
}

interface MarkedDates {
  [date: string]: {
    startingDay?: boolean
    endingDay?: boolean
    color?: string
    textColor?: string
    selected?: boolean
    disabled?: boolean
    disableTouchEvent?: boolean
  }
}

const getScale = (floorWidth: number, floorHeight: number, containerWidth: number, containerHeight: number) => {
  const scaleX = (containerWidth - 40) / floorWidth
  const scaleY = (containerHeight - 40) / floorHeight
  return Math.min(scaleX, scaleY, 1)
}

export default function VisualSpaceBrowseScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [market, setMarket] = useState<Market | null>(null)
  const [floors, setFloors] = useState<Floor[]>([])
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null)
  const [spotTypes, setSpotTypes] = useState<SpotType[]>([])
  const [spots, setSpots] = useState<Spot[]>([])
  const [spotReservations, setSpotReservations] = useState<Record<string, Reservation[]>>({})

  // Selected spot for reservation
  const [selectedSpot, setSelectedSpot] = useState<Spot | null>(null)
  const [reserveModalVisible, setReserveModalVisible] = useState(false)

  // Date selection
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectingDate, setSelectingDate] = useState<'start' | 'end'>('start')
  const [existingReservations, setExistingReservations] = useState<Reservation[]>([])
  const [loadingReservations, setLoadingReservations] = useState(false)
  const [reserving, setReserving] = useState(false)

  // Canvas
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  // Legend filter
  const [showAvailableOnly, setShowAvailableOnly] = useState(false)

  useEffect(() => {
    fetchData()
  }, [marketId])

  const fetchData = async () => {
    setLoading(true)
    try {
      // Fetch market
      const { data: marketData } = await supabase
        .from('flea_markets')
        .select('id, name')
        .eq('id', marketId)
        .single()

      if (marketData) setMarket(marketData)

      // Fetch floors
      const { data: floorsData, error: floorsError } = await supabase
        .from('floors')
        .select('*')
        .eq('flea_market_id', marketId)
        .eq('is_active', true)
        .order('sort_order')

      if (floorsError) throw floorsError
      setFloors(floorsData || [])

      if (floorsData && floorsData.length > 0) {
        setSelectedFloor(floorsData[0])
      }

      // Fetch spot types
      const { data: typesData, error: typesError } = await supabase
        .from('spot_types')
        .select('*')
        .eq('flea_market_id', marketId)
        .eq('is_active', true)

      if (typesError) throw typesError
      setSpotTypes(typesData || [])

    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch spots when floor changes
  useEffect(() => {
    if (selectedFloor) {
      fetchSpots(selectedFloor.id)
    } else {
      setSpots([])
    }
  }, [selectedFloor])

  const fetchSpots = async (floorId: string) => {
    try {
      const { data, error } = await supabase
        .from('spots')
        .select('*, spot_type:spot_types(*)')
        .eq('floor_id', floorId)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('spot_number')

      if (error) throw error
      setSpots(data || [])

      // Fetch reservations for all spots
      if (data && data.length > 0) {
        fetchSpotReservations(data.map(s => s.id))
      }
    } catch (error) {
      console.error('Error fetching spots:', error)
    }
  }

  const fetchSpotReservations = async (spotIds: string[]) => {
    const today = new Date().toISOString().split('T')[0]
    try {
      const { data, error } = await supabase
        .from('reservations')
        .select('spot_id, start_date, end_date, status')
        .in('spot_id', spotIds)
        .in('status', ['pending', 'confirmed'])
        .gte('end_date', today)

      if (error) throw error

      // Group by spot_id
      const grouped: Record<string, Reservation[]> = {}
      data?.forEach(r => {
        if (!grouped[r.spot_id]) grouped[r.spot_id] = []
        grouped[r.spot_id].push(r)
      })
      setSpotReservations(grouped)
    } catch (error) {
      console.error('Error fetching spot reservations:', error)
    }
  }

  const isSpotAvailableNow = (spotId: string): boolean => {
    const reservations = spotReservations[spotId] || []
    if (reservations.length === 0) return true

    const today = new Date().toISOString().split('T')[0]
    return !reservations.some(r => r.start_date <= today && r.end_date >= today)
  }

  const getSpotStatusColor = (spot: Spot): string => {
    if (!spot.is_available) return '#9E9E9E' // Unavailable
    const isAvailable = isSpotAvailableNow(spot.id)
    if (isAvailable) return spot.spot_type?.color || '#4CAF50'
    return '#F44336' // Reserved
  }

  const fetchExistingReservations = async (spotId: string) => {
    setLoadingReservations(true)
    try {
      const today = new Date().toISOString().split('T')[0]
      const { data, error } = await supabase
        .from('reservations')
        .select('start_date, end_date, status')
        .eq('spot_id', spotId)
        .in('status', ['pending', 'confirmed'])
        .gte('end_date', today)
        .order('start_date')

      if (error) throw error
      setExistingReservations(data || [])
    } catch (error) {
      console.error('Error fetching reservations:', error)
      setExistingReservations([])
    } finally {
      setLoadingReservations(false)
    }
  }

  const openReserveModal = (spot: Spot) => {
    setSelectedSpot(spot)
    setStartDate('')
    setEndDate('')
    setSelectingDate('start')
    setExistingReservations([])
    setReserveModalVisible(true)
    fetchExistingReservations(spot.id)
  }

  const showAlert = (title: string, message: string, onOk?: () => void) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`)
      onOk?.()
    } else {
      Alert.alert(title, message, [{ text: 'OK', onPress: onOk }])
    }
  }

  // Date checking functions
  const isDateReserved = (dateStr: string): boolean => {
    return existingReservations.some(res => {
      return dateStr >= res.start_date && dateStr <= res.end_date
    })
  }

  const doesRangeOverlap = (start: string, end: string): boolean => {
    return existingReservations.some(res => {
      return start <= res.end_date && end >= res.start_date
    })
  }

  const onDayPress = (day: DateData) => {
    const selectedDateStr = day.dateString

    if (isDateReserved(selectedDateStr)) return

    if (selectingDate === 'start') {
      setStartDate(selectedDateStr)
      if (endDate && selectedDateStr > endDate) {
        setEndDate('')
      }
      setSelectingDate('end')
    } else {
      if (selectedDateStr < startDate) {
        setStartDate(selectedDateStr)
        setEndDate('')
      } else {
        if (doesRangeOverlap(startDate, selectedDateStr)) {
          const firstBlockedDate = existingReservations
            .filter(res => res.start_date > startDate)
            .sort((a, b) => a.start_date.localeCompare(b.start_date))[0]

          if (firstBlockedDate && selectedDateStr >= firstBlockedDate.start_date) {
            const adjustedEnd = new Date(firstBlockedDate.start_date)
            adjustedEnd.setDate(adjustedEnd.getDate() - 1)
            const adjustedEndStr = adjustedEnd.toISOString().split('T')[0]
            if (adjustedEndStr >= startDate) {
              setEndDate(adjustedEndStr)
            }
            return
          }
        }
        setEndDate(selectedDateStr)
      }
    }
  }

  const getMarkedDates = (): MarkedDates => {
    const marked: MarkedDates = {}

    // Mark existing reservations as blocked
    existingReservations.forEach(res => {
      const start = new Date(res.start_date)
      const end = new Date(res.end_date)
      const current = new Date(start)

      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0]
        marked[dateStr] = {
          selected: true,
          startingDay: dateStr === res.start_date,
          endingDay: dateStr === res.end_date,
          color: '#FFCDD2',
          textColor: '#999',
          disabled: true,
          disableTouchEvent: true }
        current.setDate(current.getDate() + 1)
      }
    })

    // Mark user selection
    if (startDate && !endDate) {
      marked[startDate] = {
        selected: true,
        startingDay: true,
        endingDay: true,
        color: '#4A90A4',
        textColor: '#fff' }
    }

    if (startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)
      const current = new Date(start)

      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0]
        const isStart = dateStr === startDate
        const isEnd = dateStr === endDate

        marked[dateStr] = {
          selected: true,
          startingDay: isStart,
          endingDay: isEnd,
          color: isStart || isEnd ? '#4A90A4' : '#B8D4E3',
          textColor: isStart || isEnd ? '#fff' : '#333' }

        current.setDate(current.getDate() + 1)
      }
    }

    return marked
  }

  const calculateDays = () => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
  }

  const calculatePrice = () => {
    if (!selectedSpot?.spot_type || !startDate || !endDate) return null

    const spotType = selectedSpot.spot_type
    const days = calculateDays()
    if (days <= 0) return null

    if (spotType.price_per_month && days >= 28) {
      const months = days / 30
      return (spotType.price_per_month * months).toFixed(2)
    }
    if (spotType.price_per_week && days >= 7) {
      const weeks = days / 7
      return (spotType.price_per_week * weeks).toFixed(2)
    }
    if (spotType.price_per_day) {
      return (spotType.price_per_day * days).toFixed(2)
    }
    return null
  }

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric' })
  }

  const submitReservation = async () => {
    if (!selectedSpot || !startDate || !endDate) return

    if (!user) {
      showAlert(
        t('login_required') || 'Login Required',
        t('login_to_reserve') || 'Please log in to make a reservation'
      )
      setReserveModalVisible(false)
      navigation.navigate('Login')
      return
    }

    const price = calculatePrice()
    if (!price) {
      showAlert(t('error'), t('invalid_dates') || 'Invalid dates selected')
      return
    }

    setReserving(true)
    try {
      const { data, error } = await supabase
        .from('reservations')
        .insert({
          spot_id: selectedSpot.id,
          seller_id: user.id,
          flea_market_id: marketId,
          start_date: startDate,
          end_date: endDate,
          base_price: parseFloat(price),
          commission_percentage: 0,
          status: 'confirmed' })
        .select()
        .single()

      if (error) throw error

      setReserveModalVisible(false)

      const successTitle = t('success') || 'Success'
      const successMsg = t('reservation_created') || 'Space reserved successfully!'

      showAlert(successTitle, successMsg, () => {
        // Refresh data
        if (selectedFloor) fetchSpots(selectedFloor.id)
      })

    } catch (error: any) {
      console.error('Reservation error:', error)
      const errorMsg = error.message?.includes('overlapping')
        ? (t('space_already_reserved') || 'This space is already reserved for those dates')
        : (t('reservation_failed') || 'Failed to submit reservation')
      showAlert(t('error'), errorMsg)
    } finally {
      setReserving(false)
    }
  }

  // Render the floor plan
  const renderFloorPlan = () => {
    if (!selectedFloor || spots.length === 0) {
      return (
        <View style={styles.emptyState}>
          <View style={styles.emptyIconContainer}>
            <Store size={48} color="#4A90A4" />
          </View>
          <Text style={styles.emptyText}>
            {floors.length === 0
              ? (t('no_floor_plan') || 'No floor plan available for this market')
              : (t('no_spots_on_floor') || 'No spots on this floor')
            }
          </Text>
        </View>
      )
    }

    const scale = getScale(
      selectedFloor.width_cm,
      selectedFloor.height_cm,
      containerSize.width,
      containerSize.height
    )

    const canvasWidth = selectedFloor.width_cm * scale
    const canvasHeight = selectedFloor.height_cm * scale

    const filteredSpots = showAvailableOnly
      ? spots.filter(s => s.is_available && isSpotAvailableNow(s.id))
      : spots

    return (
      <View style={[styles.canvas, { width: canvasWidth, height: canvasHeight }]}>
        <Svg width={canvasWidth} height={canvasHeight}>
          {/* Grid lines (subtle) */}
          {Array.from({ length: Math.ceil(selectedFloor.width_cm / 100) + 1 }).map((_, i) => (
            <Line
              key={`v-${i}`}
              x1={i * 100 * scale}
              y1={0}
              x2={i * 100 * scale}
              y2={canvasHeight}
              stroke="#f0f0f0"
              strokeWidth={1}
            />
          ))}
          {Array.from({ length: Math.ceil(selectedFloor.height_cm / 100) + 1 }).map((_, i) => (
            <Line
              key={`h-${i}`}
              x1={0}
              y1={i * 100 * scale}
              x2={canvasWidth}
              y2={i * 100 * scale}
              stroke="#f0f0f0"
              strokeWidth={1}
            />
          ))}

          {/* Spots */}
          {filteredSpots.map((spot) => {
            const spotType = spot.spot_type || spotTypes.find(t => t.id === spot.spot_type_id)
            if (!spotType) return null

            const isAvailable = spot.is_available && isSpotAvailableNow(spot.id)
            const statusColor = getSpotStatusColor(spot)
            const width = spotType.width_cm * scale
            const height = spotType.depth_cm * scale
            const x = spot.x_position * scale
            const y = spot.y_position * scale

            const rotated = spot.rotation === 90 || spot.rotation === 270
            const displayWidth = rotated ? height : width
            const displayHeight = rotated ? width : height

            return (
              <G key={spot.id}>
                <Rect
                  x={x}
                  y={y}
                  width={displayWidth}
                  height={displayHeight}
                  fill={statusColor}
                  opacity={isAvailable ? 0.85 : 0.4}
                  stroke={isAvailable ? statusColor : '#999'}
                  strokeWidth={1}
                  rx={4}
                  onPress={() => {
                    if (isAvailable) {
                      openReserveModal(spot)
                    }
                  }}
                />
                <SvgText
                  x={x + displayWidth / 2}
                  y={y + displayHeight / 2}
                  fill="#fff"
                  fontSize={Math.min(displayWidth, displayHeight) * 0.3}
                  fontWeight="bold"
                  textAnchor="middle"
                  alignmentBaseline="middle"
                >
                  {spot.spot_number}
                </SvgText>
                {/* Availability indicator */}
                {!isAvailable && (
                  <SvgText
                    x={x + displayWidth / 2}
                    y={y + displayHeight / 2 + Math.min(displayWidth, displayHeight) * 0.25}
                    fill="#fff"
                    fontSize={Math.min(displayWidth, displayHeight) * 0.15}
                    textAnchor="middle"
                    alignmentBaseline="middle"
                  >
                    {t('reserved') || 'Reserved'}
                  </SvgText>
                )}
              </G>
            )
          })}
        </Svg>
      </View>
    )
  }

  const today = new Date().toISOString().split('T')[0]

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>{t('select_space') || 'Select a Space'}</Text>
          {market && <Text style={styles.headerSubtitle}>{market.name}</Text>}
        </View>
      </View>

      {/* Floor tabs */}
      {floors.length > 1 && (
        <View style={styles.floorTabs}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.floorTabsContent}>
            {floors.map((floor) => (
              <TouchableOpacity
                key={floor.id}
                style={[styles.floorTab, selectedFloor?.id === floor.id && styles.floorTabActive]}
                onPress={() => setSelectedFloor(floor)}
              >
                <Text style={[styles.floorTabText, selectedFloor?.id === floor.id && styles.floorTabTextActive]}>
                  {floor.name}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Legend & Filter */}
      <View style={styles.legendBar}>
        <View style={styles.legendItems}>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: '#4CAF50' }]} />
            <Text style={styles.legendText}>{t('available') || 'Available'}</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: '#F44336' }]} />
            <Text style={styles.legendText}>{t('reserved') || 'Reserved'}</Text>
          </View>
        </View>
        <TouchableOpacity
          style={[styles.filterToggle, showAvailableOnly && styles.filterToggleActive]}
          onPress={() => setShowAvailableOnly(!showAvailableOnly)}
        >
          <Text style={[styles.filterToggleText, showAvailableOnly && styles.filterToggleTextActive]}>
            {t('show_available_only') || 'Available only'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Floor plan canvas */}
      <View
        style={styles.canvasWrapper}
        onLayout={(e) => {
          const { width, height } = e.nativeEvent.layout
          setContainerSize({ width, height })
        }}
      >
        {renderFloorPlan()}
      </View>

      {/* Spot type legend */}
      {spotTypes.length > 0 && (
        <View style={styles.typeLegend}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.typeLegendContent}>
            {spotTypes.map((type) => (
              <View key={type.id} style={styles.typeLegendItem}>
                <View style={[styles.typeLegendColor, { backgroundColor: type.color }]} />
                <Text style={styles.typeLegendText}>{type.name}</Text>
                {type.price_per_day && (
                  <Text style={styles.typeLegendPrice}>€{type.price_per_day}/d</Text>
                )}
              </View>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Tap hint */}
      <View style={styles.hintBar}>
        <Text style={styles.hintText}>
          {t('tap_spot_to_reserve') || 'Tap an available spot to reserve it'}
        </Text>
      </View>

      {/* Reservation Modal */}
      <Modal
        visible={reserveModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setReserveModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>{t('reserve_space') || 'Reserve Space'}</Text>
                <TouchableOpacity onPress={() => setReserveModalVisible(false)} style={styles.modalCloseButton}>
                  <X size={24} color="#999" />
                </TouchableOpacity>
              </View>

              {selectedSpot && selectedSpot.spot_type && (
                <>
                  <View style={styles.modalSpotInfo}>
                    <View style={[styles.spotPreview, { backgroundColor: selectedSpot.spot_type.color }]}>
                      <Text style={styles.spotPreviewText}>{selectedSpot.spot_number}</Text>
                    </View>
                    <View style={styles.spotInfoText}>
                      <Text style={styles.modalSpotName}>{selectedSpot.spot_number}</Text>
                      <Text style={styles.modalSpotType}>{selectedSpot.spot_type.name}</Text>
                      <Text style={styles.modalSpotSize}>
                        {selectedSpot.spot_type.width_cm} × {selectedSpot.spot_type.depth_cm} cm
                      </Text>
                    </View>
                  </View>

                  {/* Date Selection */}
                  <View style={styles.dateSelectionRow}>
                    <TouchableOpacity
                      style={[styles.dateButton, selectingDate === 'start' && styles.dateButtonActive]}
                      onPress={() => setSelectingDate('start')}
                    >
                      <Text style={styles.dateButtonLabel}>{t('start_date') || 'Start'}</Text>
                      <Text style={[styles.dateButtonValue, !startDate && styles.dateButtonPlaceholder]}>
                        {startDate ? formatDisplayDate(startDate) : t('select') || 'Select'}
                      </Text>
                    </TouchableOpacity>

                    <View style={styles.dateArrowContainer}>
                      <ArrowRight size={20} color="#999" />
                    </View>

                    <TouchableOpacity
                      style={[styles.dateButton, selectingDate === 'end' && styles.dateButtonActive]}
                      onPress={() => setSelectingDate('end')}
                    >
                      <Text style={styles.dateButtonLabel}>{t('end_date') || 'End'}</Text>
                      <Text style={[styles.dateButtonValue, !endDate && styles.dateButtonPlaceholder]}>
                        {endDate ? formatDisplayDate(endDate) : t('select') || 'Select'}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {/* Calendar */}
                  <View style={styles.calendarContainer}>
                    {loadingReservations ? (
                      <View style={styles.calendarLoading}>
                        <Spinner size="small" />
                        <Text style={styles.calendarLoadingText}>
                          {t('loading_availability') || 'Loading availability...'}
                        </Text>
                      </View>
                    ) : (
                      <Calendar
                        onDayPress={onDayPress}
                        markedDates={getMarkedDates()}
                        markingType="period"
                        minDate={today}
                        theme={{
                          backgroundColor: '#ffffff',
                          calendarBackground: '#ffffff',
                          textSectionTitleColor: '#666',
                          selectedDayBackgroundColor: '#4A90A4',
                          selectedDayTextColor: '#ffffff',
                          todayTextColor: '#4A90A4',
                          dayTextColor: '#333',
                          textDisabledColor: '#ccc',
                          arrowColor: '#4A90A4',
                          monthTextColor: '#333',
                          textDayFontWeight: '500',
                          textMonthFontWeight: 'bold',
                          textDayHeaderFontWeight: '500',
                          textDayFontSize: 16,
                          textMonthFontSize: 18,
                          textDayHeaderFontSize: 14 }}
                      />
                    )}
                  </View>

                  {/* Price summary */}
                  {startDate && endDate && (
                    <View style={styles.summarySection}>
                      <View style={styles.summaryRow}>
                        <Text style={styles.summaryLabel}>{t('duration') || 'Duration'}</Text>
                        <Text style={styles.summaryValue}>
                          {calculateDays()} {calculateDays() === 1 ? (t('day') || 'day') : (t('days') || 'days')}
                        </Text>
                      </View>
                      {calculatePrice() && (
                        <View style={styles.priceCalculation}>
                          <Text style={styles.priceLabel}>{t('total_price') || 'Total Price'}</Text>
                          <Text style={styles.calculatedPrice}>€{calculatePrice()}</Text>
                        </View>
                      )}
                    </View>
                  )}

                  {/* Pricing info */}
                  <View style={styles.pricingInfo}>
                    <Text style={styles.pricingTitle}>{t('pricing') || 'Pricing'}:</Text>
                    {selectedSpot.spot_type.price_per_day && (
                      <Text style={styles.pricingRow}>• €{selectedSpot.spot_type.price_per_day} {t('per_day') || '/day'}</Text>
                    )}
                    {selectedSpot.spot_type.price_per_week && (
                      <Text style={styles.pricingRow}>• €{selectedSpot.spot_type.price_per_week} {t('per_week') || '/week'}</Text>
                    )}
                    {selectedSpot.spot_type.price_per_month && (
                      <Text style={styles.pricingRow}>• €{selectedSpot.spot_type.price_per_month} {t('per_month') || '/month'}</Text>
                    )}
                  </View>

                  <TouchableOpacity
                    style={[
                      styles.reserveButton,
                      (!startDate || !endDate || reserving) && styles.reserveButtonDisabled,
                    ]}
                    onPress={submitReservation}
                    disabled={!startDate || !endDate || reserving}
                  >
                    {reserving ? (
                      <Spinner color="#fff" />
                    ) : (
                      <Text style={styles.reserveButtonText}>
                        {t('reserve_now') || 'Reserve Now'}
                      </Text>
                    )}
                  </TouchableOpacity>
                </>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa' },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee' },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center' },
  headerTitleContainer: {
    flex: 1,
    marginLeft: 8 },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333' },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 2 },
  floorTabs: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee' },
  floorTabsContent: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8 },
  floorTab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f0f0f0' },
  floorTabActive: {
    backgroundColor: '#4A90A4' },
  floorTabText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500' },
  floorTabTextActive: {
    color: '#fff' },
  legendBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee' },
  legendItems: {
    flexDirection: 'row',
    gap: 16 },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6 },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6 },
  legendText: {
    fontSize: 13,
    color: '#666' },
  filterToggle: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f0f0f0' },
  filterToggleActive: {
    backgroundColor: '#4A90A4' },
  filterToggleText: {
    fontSize: 13,
    color: '#666' },
  filterToggleTextActive: {
    color: '#fff' },
  canvasWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16 },
  canvas: {
    backgroundColor: '#fff',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    overflow: 'hidden' },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32 },
  emptyIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(74, 144, 164, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16 },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center' },
  typeLegend: {
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee' },
  typeLegendContent: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 16 },
  typeLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6 },
  typeLegendColor: {
    width: 16,
    height: 16,
    borderRadius: 4 },
  typeLegendText: {
    fontSize: 13,
    color: '#333',
    fontWeight: '500' },
  typeLegendPrice: {
    fontSize: 12,
    color: '#666' },
  hintBar: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#E3F2FD',
    alignItems: 'center' },
  hintText: {
    fontSize: 14,
    color: '#1976D2' },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    maxHeight: '90%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20 },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333' },
  modalCloseButton: {
    padding: 4 },
  modalSpotInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    gap: 16 },
  spotPreview: {
    width: 60,
    height: 60,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center' },
  spotPreviewText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold' },
  spotInfoText: {
    flex: 1 },
  modalSpotName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333' },
  modalSpotType: {
    fontSize: 14,
    color: '#666',
    marginTop: 2 },
  modalSpotSize: {
    fontSize: 13,
    color: '#888',
    marginTop: 2 },
  dateSelectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16 },
  dateButton: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'transparent' },
  dateButtonActive: {
    borderColor: '#4A90A4',
    backgroundColor: '#E8F4F7' },
  dateButtonLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4 },
  dateButtonValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333' },
  dateButtonPlaceholder: {
    color: '#999' },
  dateArrowContainer: {
    marginHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center' },
  calendarContainer: {
    marginBottom: 16,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#eee' },
  calendarLoading: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center' },
  calendarLoadingText: {
    marginTop: 8,
    fontSize: 14,
    color: '#666' },
  summarySection: {
    marginBottom: 16 },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8 },
  summaryLabel: {
    fontSize: 14,
    color: '#666' },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333' },
  priceCalculation: {
    backgroundColor: '#E8F5E9',
    padding: 16,
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8 },
  priceLabel: {
    fontSize: 16,
    color: '#333',
    fontWeight: '500' },
  calculatedPrice: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4CAF50' },
  pricingInfo: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    marginBottom: 20 },
  pricingTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 6 },
  pricingRow: {
    fontSize: 13,
    color: '#888',
    marginTop: 2 },
  reserveButton: {
    backgroundColor: '#4A90A4',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: 20 },
  reserveButtonDisabled: {
    opacity: 0.5 },
  reserveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' } })
